'use client'

import { useEffect, useState } from 'react'

import {
  cn,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@rewardops/ui'

type NavigationSelectProps = {
  id: string
  options: { key: string; value: React.ReactNode }[]
  placeholder: React.ReactNode
  onOptionSelect: (option: string) => void
  className?: string
  contentClassName?: string
  optionClassName?: string
}

export const MapleNavigationDropdown = ({
  id,
  options,
  placeholder,
  onOptionSelect,
  className,
  contentClassName,
  optionClassName,
}: NavigationSelectProps) => {
  const [open, setOpen] = useState(false)
  const [hoverIndex, setHoverIndex] = useState<number | null>(null)

  useEffect(() => {
    const body = document.body

    if (open) {
      // Remove the data-scroll-locked attribute when the dropdown is open
      body.removeAttribute('data-scroll-locked')
    }

    const observer = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'data-scroll-locked'
        ) {
          body.removeAttribute('data-scroll-locked')
        }
      })
    })

    // Observe changes to attributes on the body
    observer.observe(body, { attributes: true })

    return () => {
      observer.disconnect() // Disconnect observer on component unmount
      body.setAttribute('data-scroll-locked', 'true') // Optionally reset attribute
    }
  }, [open])

  return (
    <DropdownMenu open={open} onOpenChange={setOpen}>
      <DropdownMenuTrigger
        className={cn(
          'm-0 w-full cursor-pointer p-0 text-left',
          'hover:text-tenant-primary-color focus:outline-none',
          open && 'text-tenant-primary-color',
          'flex h-full w-full items-center justify-center',
          className,
        )}
      >
        {placeholder}
      </DropdownMenuTrigger>
      <DropdownMenuContent
        className={cn(
          'z-50 mt-2 w-32 min-w-[9rem] overflow-hidden rounded-md border bg-white py-1 shadow-lg shadow-md',
          'data-[state=open]:animate-in data-[state=closed]:animate-out',
          'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
          'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
          'data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2',
          'data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
          contentClassName,
        )}
        align="start"
      >
        {options.map((option, index) => (
          <DropdownMenuItem
            key={option.key}
            className={cn(
              'cursor-pointer px-4 py-2 text-sm font-bold text-gray-700',
              'hover:bg-white hover:font-bold hover:text-tenant-primary-color',
              'focus:outline-none',
              optionClassName, // Merge custom class here
            )}
            onMouseEnter={() => setHoverIndex(index)}
            onMouseLeave={() => setHoverIndex(null)}
            onSelect={() => {
              onOptionSelect(option.key)
              setOpen(false)
              setHoverIndex(null)
            }}
          >
            <span
              className={cn(
                hoverIndex === index
                  ? 'border-b-2 border-tenant-primary-color text-tenant-primary-color'
                  : 'border-b-2 border-transparent text-gray-700',
              )}
            >
              {option.value}
            </span>
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
